(() => {
  if (window.robotPackageUpgradesLoaded) return;
  window.robotPackageUpgradesLoaded = true;

  const clean = value =>
    String(value ?? '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const normal = value => clean(value).toLowerCase();

  function tidyTitle(value) {
    return clean(value)
      .replace(/[:*]\s*$/, '')
      .replace(
        /^(configure|select|choose|add)\s+(your\s+|a\s+|an\s+)?/i,
        ''
      );
  }

  function shortTitle(value) {
    let title = tidyTitle(value);

    // Shorten product-specific configurator titles.
    title = title.replace(/^Robot Craftsman\s+/i, '');

    title = title.replace(
      /^.*?\b(?:R3[2345]|S550|S650|GT86|BRZ|997|991|992)\b\s+/i,
      ''
    );

    return title || tidyTitle(value) || 'Upgrade';
  }

  function shortOption(value) {
    return clean(value)
      .replace(/\s*\(\s*[+]?[£$€][^)]*\)/g, '')
      .replace(/^.*?\s+—\s+/, '')
      .trim();
  }

  function isNone(value, label = value) {
    const v = normal(value);
    const t = normal(label);

    return (
      !v ||
      /^(0|none|null|false)$/i.test(v) ||
      /^(none|no|no thanks|no thank you|not selected|not required|no upgrade|no bonnet|no spoiler|no wing|select.*|choose.*|please select.*)$/i.test(t)
    );
  }

  function findControls(component) {
    const form = component.findForm?.();
    const section = form?.closest(
      'section[data-product-handle]'
    );

    const local = new Set([
      ...(form?.querySelectorAll('.bundled-addon-input') || []),
      ...(section?.querySelectorAll('.bundled-addon-input') || [])
    ]);

    if (local.size) return [...local];

    if (
      document.querySelectorAll('robot-selected-package').length === 1
    ) {
      return [
        ...document.querySelectorAll(
          '.custom-addon-wrapper .bundled-addon-input'
        )
      ];
    }

    return [];
  }

  function getTitle(control, wrapper) {
    const explicit =
      control.dataset.addonTitle ||
      control.dataset.productTitle ||
      control.dataset.title ||
      wrapper?.dataset.addonTitle ||
      wrapper?.dataset.productTitle ||
      wrapper?.dataset.title;

    if (explicit) return tidyTitle(explicit);

    if (control.tagName === 'SELECT' && control.labels?.length) {
      return tidyTitle(control.labels[0].textContent);
    }

    const labelledBy = control.getAttribute('aria-labelledby');

    if (labelledBy) {
      const title = labelledBy
        .split(/\s+/)
        .map(id => document.getElementById(id)?.textContent || '')
        .join(' ');

      if (clean(title)) return tidyTitle(title);
    }

    let parent = control.parentElement;

    while (parent) {
      const candidates = parent.querySelectorAll(
        'legend,h2,h3,h4,h5,h6,' +
        '[data-addon-title],.addon-title,.addon-label,' +
        '.custom-addon-title,.custom-addon-label'
      );

      for (const candidate of candidates) {
        if (candidate.closest('robot-selected-package')) continue;

        const title = tidyTitle(candidate.textContent);

        if (
          title &&
          title.length < 180 &&
          !/^(configure upgrades|configurable upgrades|upgrades|optional)$/i.test(title)
        ) {
          return title;
        }
      }

      if (parent === wrapper) break;
      parent = parent.parentElement;
    }

    return (
      tidyTitle(control.getAttribute('aria-label')) ||
      'Upgrade'
    );
  }

  function getRadioLabel(control) {
    return clean(
      control.dataset.optionTitle ||
      control.dataset.variantTitle ||
      control.labels?.[0]?.textContent ||
      control.value
    );
  }

  function collectGroups(component) {
    const groups = new Map();

    findControls(component).forEach(control => {
      if (control.disabled) return;

      const wrapper = control.closest('.custom-addon-wrapper');
      const originalTitle = getTitle(control, wrapper);
      const title = shortTitle(originalTitle);

      // One entry per configurable part, not per choice.
      const groupKey =
        normal(originalTitle) +
        (control.type === 'radio'
          ? ':' + (control.name || '')
          : '');

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          title,
          selected: false,
          option: '',
          hasChoices: false
        });
      }

      const group = groups.get(groupKey);

      if (control.tagName === 'SELECT') {
        const available = [...control.options].filter(
          option => !option.disabled
        );

        if (!available.length) return;

        group.hasChoices = true;

        const selected = control.selectedOptions[0];

        if (
          selected &&
          !isNone(selected.value, selected.textContent)
        ) {
          group.selected = true;

          group.option = shortOption(
            selected.dataset.productTitle ||
            selected.dataset.addonTitle ||
            selected.textContent
          );
        }

        return;
      }

      if (
        control.tagName === 'INPUT' &&
        ['radio', 'checkbox'].includes(control.type)
      ) {
        group.hasChoices = true;

        const label = getRadioLabel(control);

        if (
          control.checked &&
          !isNone(control.value, label)
        ) {
          group.selected = true;
          group.option = shortOption(label);
        }
      }
    });

    return [...groups.values()].filter(
      group => group.hasChoices
    );
  }

  function makeRow(group, status = true) {
    const row = document.createElement('li');
    row.className = group.selected
      ? 'rp-upgrade-row rp-upgrade-yes'
      : 'rp-upgrade-row rp-upgrade-no';

    const icon = document.createElement('span');
    icon.className = 'rp-upgrade-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = group.selected ? '✓' : '×';

    const content = document.createElement('span');
    content.className = 'rp-upgrade-name';

    const name = document.createElement('strong');
    name.textContent = group.title;

    content.append(name);

    const detail = document.createElement('span');
    detail.className = 'rp-upgrade-detail';

    detail.textContent = group.selected
      ? group.option
      : 'Not selected';

    content.append(detail);
    row.append(icon, content);

    if (status) {
      const badge = document.createElement('span');
      badge.className = 'rp-upgrade-status';

      badge.textContent = group.selected
        ? 'Selected'
        : 'Not selected';

      row.append(badge);
    }

    return row;
  }

  function getAccordion(component) {
    const target = component.querySelector(
      '[data-package-accordions]'
    );

    if (!target) return null;

    let accordion = target.querySelector(
      '[data-rp-available-upgrades]'
    );

    if (accordion) return accordion;

    accordion = [...target.querySelectorAll('details')]
      .find(details =>
        normal(details.querySelector('summary')?.textContent)
          === 'available upgrades'
      );

    if (!accordion) {
      accordion = document.createElement('details');
      accordion.className = 'rp-accordion';

      const summary = document.createElement('summary');
      summary.textContent = 'Available upgrades';

      const body = document.createElement('div');
      body.className = 'rp-accordion-body rte';

      accordion.append(summary, body);
      target.append(accordion);
    }

    accordion.setAttribute('data-rp-available-upgrades', '');
    return accordion;
  }

  function updateAvailable(component, groups) {
    const existing = component.querySelector(
      '[data-rp-available-upgrades]'
    );

    if (!groups.length) {
      if (existing?.dataset.rpGenerated === 'true') {
        existing.hidden = true;
      }
      return;
    }

    const accordion = getAccordion(component);
    if (!accordion) return;

    accordion.hidden = false;
    accordion.dataset.rpGenerated = 'true';

    const signature = JSON.stringify(groups);

    if (accordion.dataset.signature === signature) return;
    accordion.dataset.signature = signature;

    const body = accordion.querySelector(
      '.rp-accordion-body'
    );

    if (!body) return;

    const list = document.createElement('ul');
    list.className = 'rp-upgrade-list rp-upgrade-grouped';

    groups.forEach(group => {
      list.append(makeRow(group));
    });

    body.replaceChildren(list);
  }

  function updateSelected(component, groups) {
    const contents = component.querySelector(
      '[data-package-contents]'
    );

    if (!contents) return;

    const selected = groups.filter(group => group.selected);

    let summary = component.querySelector(
      '[data-rp-selected-upgrades]'
    );

    if (!selected.length) {
      summary?.remove();
      return;
    }

    if (!summary) {
      summary = document.createElement('div');
      summary.className = 'rp-selected-upgrades';
      summary.setAttribute('data-rp-selected-upgrades', '');
    }

    if (contents.nextElementSibling !== summary) {
      contents.after(summary);
    }

    const signature = JSON.stringify(selected);

    if (summary.dataset.signature === signature) return;
    summary.dataset.signature = signature;

    const heading = document.createElement('h3');
    heading.className = 'rp-selected-upgrades-heading';
    heading.textContent = 'Your selected upgrades';

    const list = document.createElement('ul');
    list.className = 'rp-upgrade-list';

    selected.forEach(group => {
      list.append(makeRow(group, false));
    });

    summary.replaceChildren(heading, list);
  }

  function refreshComponent(component) {
    const built = component.querySelector(
      '[data-package-built]'
    );

    if (!built || built.hidden) return;

    const groups = collectGroups(component);

    updateSelected(component, groups);
    updateAvailable(component, groups);
  }

  function refresh() {
    document.querySelectorAll('robot-selected-package')
      .forEach(refreshComponent);
  }

  let timer;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 100);
  }

  document.addEventListener('change', schedule);
  document.addEventListener('input', schedule);
  document.addEventListener('robot:package-updated', schedule);
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      refresh,
      { once: true }
    );
  } else {
    refresh();
  }

  setInterval(refresh, 1000);
})();